import { CheckIcon } from '@/components/icons';
import { Box, LinearProgress, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { TabularText } from '@/components/TabularText';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import { LiveRegion } from '@/components/LiveRegion';
import { resolveTokens } from '@/theme/theme';

export type ImportStepState = 'done' | 'active' | 'pending' | 'failed';

export interface ImportProgressStep {
  label: string;
  state: ImportStepState;
  /** Secondary line, shown once the step is active or done. */
  note?: string;
}

interface ImportProgressProps {
  /** Status line under the bar. Not a repeat of the active step. */
  title: string;
  /** Polite announcement. Stage text only — never a percent. */
  announcement: string;
  steps: ImportProgressStep[];
  bar: 'determinate' | 'indeterminate' | 'none';
  percent?: number;
  /** Tabular count beside the status line (imported rows, sample totals). */
  count?: string;
}

/**
 * Import progress (S:401-425): steps, then a 4px bar, then a status line with an optional
 * tabular count. The live region changes only when `announcement` changes; it mounts empty and
 * takes its text after mount, so the first stage is announced too (TMMAPP-0097 B10). An
 * indeterminate bar becomes a static bar under reduced motion. A failed step stops the bar: it is
 * static and no longer `aria-busy`. The active step has a visible mark, not a spinner by itself.
 */
export function ImportProgress({
  title,
  announcement,
  steps,
  bar,
  percent = 0,
  count,
}: ImportProgressProps) {
  const { t } = useTranslation();
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)');
  const working = t('imports.working');
  const failed = steps.some((step) => step.state === 'failed');
  const indeterminate = bar === 'indeterminate' && !failed;
  const staticBar = indeterminate && reduced;
  const showBar = bar !== 'none';

  return (
    <Stack spacing={1.5}>
      <Stack component="ul" spacing={1.25} sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {steps.map((step) => (
          <Stack
            key={step.label}
            component="li"
            direction="row"
            sx={{ gap: 1.5, alignItems: 'flex-start' }}
          >
            <StepMark state={step.state} />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: step.state === 'active' ? 500 : 400,
                  color: step.state === 'pending' ? 'text.secondary' : 'text.primary',
                }}
              >
                {step.label}
              </Typography>
              {step.note != null && step.note.length > 0 && step.state !== 'pending' && (
                <Typography variant="caption" component="span" sx={{ display: 'block' }}>
                  {step.note}
                </Typography>
              )}
            </Box>
          </Stack>
        ))}
      </Stack>
      {showBar && (
        <LinearProgress
          variant={indeterminate && !staticBar ? 'indeterminate' : 'determinate'}
          value={staticBar ? 100 : percent}
          aria-label={title}
          aria-busy={!failed && (indeterminate || (bar === 'determinate' && percent < 100))}
          aria-valuetext={staticBar ? working : undefined}
          sx={{ height: 4 }}
        />
      )}
      {staticBar && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {working}
        </Typography>
      )}
      <Stack
        direction="row"
        sx={{ justifyContent: 'space-between', gap: 1, alignItems: 'baseline' }}
      >
        <Typography variant="body2">{title}</Typography>
        {count != null && count.length > 0 && <TabularText>{count}</TabularText>}
      </Stack>
      <LiveRegion text={announcement} />
    </Stack>
  );
}

/**
 * Slim bar for a preview that is already on screen. The label stays; motion adds "Working…".
 * `overlay` lays it over the top edge of the preview it refreshes, so the page does not shift
 * (TMMAPP-0097 B10): the label is then for assistive technology only. The parent is positioned.
 */
export function ImportActivityBar({
  label,
  overlay = false,
}: {
  label: string;
  overlay?: boolean;
}) {
  const { t } = useTranslation();
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)');
  const working = t('imports.working');
  return (
    <Stack
      spacing={0.5}
      sx={
        overlay
          ? { position: 'absolute', top: 0, insetInline: 0, zIndex: 1, pointerEvents: 'none' }
          : undefined
      }
    >
      <Typography variant="body2" sx={overlay ? visuallyHidden : undefined}>
        {label}
      </Typography>
      {reduced && (
        <Typography variant="body2" sx={overlay ? visuallyHidden : { color: 'text.secondary' }}>
          {working}
        </Typography>
      )}
      <LinearProgress
        variant={reduced ? 'determinate' : 'indeterminate'}
        value={reduced ? 100 : undefined}
        aria-label={label}
        aria-busy
        aria-valuetext={reduced ? working : undefined}
        sx={{ height: 4 }}
      />
      <LiveRegion text={label} />
    </Stack>
  );
}

function StepMark({ state }: { state: ImportStepState }) {
  const tokens = resolveTokens(useTheme());
  if (state === 'done') {
    return <CheckIcon size={20} color={tokens.brandBoundary} />;
  }
  if (state === 'failed') {
    return (
      <Box
        aria-hidden
        component="span"
        sx={{
          width: 16,
          textAlign: 'center',
          color: tokens.text,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        !
      </Box>
    );
  }
  if (state === 'active') {
    return (
      <Box
        aria-hidden
        sx={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          bgcolor: tokens.brandBoundary,
          flexShrink: 0,
        }}
      />
    );
  }
  return (
    <Box
      aria-hidden
      sx={{
        width: 16,
        height: 16,
        border: '2px solid',
        borderColor: tokens.lineStrong,
        borderRadius: '50%',
        flexShrink: 0,
      }}
    />
  );
}
